<template>
  <div :class="['pdv-checkout-shell', { 'host-page': host === 'page', 'host-modal': host === 'modal' }]">
    <!-- Header: Back button + Client + Order info -->
    <div class="checkout-header" v-if="order" data-tutorial="checkout-header">
      <Button
        v-if="host === 'page'"
        :label="t('Voltar')"
        icon="pi pi-arrow-left"
        size="small"
        severity="secondary"
        data-tutorial="checkout-btn-voltar"
        data-testid="checkout-voltar-btn"
        @click="handleVoltar" />
      <div class="client-info">
        <i class="pi pi-user"></i>
        <span
          class="client-name"
          :class="{ 'client-name-clickable': order.so_order_client_id }"
          data-tutorial="checkout-client-name"
          @click="handleClientClick">
          {{ order.so_order_client_name || t('Sem cliente') }}
        </span>
        <span class="client-phone" v-if="formattedClientPhone" data-tutorial="checkout-client-phone">{{ formattedClientPhone }}</span>
        <Button
          v-if="lastWhatsAppPayload"
          icon="pi pi-whatsapp"
          severity="success"
          text
          rounded
          size="small"
          :title="t('Reenviar WhatsApp')"
          data-testid="checkout-reenviar-whatsapp-btn"
          @click="reenviarWhatsApp"
        />
      </div>
      <div class="order-info">
        <span class="order-number" data-tutorial="checkout-order-number">OS #{{ order.so_order_id }}</span>
        <span class="order-status" :class="'status-' + order.so_order_status">
          {{ checkout.statusLabel(order.so_order_status) }}
        </span>
      </div>
    </div>

    <!-- Main layout: Items (left) + Payment (right) -->
    <div class="checkout-layout" v-if="order">
      <!-- Left: Items -->
      <div class="checkout-items">
        <div class="section-header">
          <h3>{{ t('Itens da Ordem') }}</h3>
          <div class="item-actions" v-if="checkout.canAddItems.value" data-tutorial="checkout-item-actions">
            <Button :label="t('Serviço')" icon="pi pi-plus" size="small" severity="info" data-testid="checkout-add-servico-btn" @click="handleAddServico" />
            <Button :label="t('Produtos')" icon="pi pi-plus" size="small" severity="secondary" data-testid="checkout-add-produtos-btn" @click="handleAddProdutos" />
            <app-button
              v-if="combosAtivos"
              :label="t('Vender combo')"
              icon="pi pi-plus"
              size="small"
              severity="secondary"
              :disabled="!order.so_order_client_id"
              :explanation="order.so_order_client_id ? EX_COMBO.venderCombo : EX_COMBO.venderSemCliente"
              data-testid="checkout-vender-combo-btn"
              @click="handleVenderCombo" />
          </div>
        </div>

        <!-- Editing a non-draft / partially-received OS can affect the total; warn
             the user. Lowering edits below the received amount are rejected by the
             backend received-floor invariant. The text is payment-aware: the
             recebimentos/floor claim only appears when recebimentos actually exist. -->
        <InlineMessage
          v-if="showPaidEditWarning"
          severity="warn"
          :closable="false"
          class="paid-edit-warning"
          data-testid="checkout-paid-edit-warning">
          {{ paidEditWarningText }}
        </InlineMessage>

        <div class="items-list" v-if="groupedItems.length > 0" data-tutorial="checkout-items-list">
          <DataTable
            :config="itemsTableConfig"
            :data="decoratedGroupedItems"
            @button-click="handleTableButtonClick"
          />
        </div>
        <div class="empty-items" v-else>
          <i class="pi pi-inbox"></i>
          <p>{{ t('Nenhum item adicionado') }}</p>
        </div>

        <!-- Totals -->
        <div class="totals-section" data-tutorial="checkout-totals">
          <div class="total-row" v-if="order.so_order_discount_value">
            <span>{{ t('Desconto') }}</span>
            <span class="discount">-{{ n(discountAmount / 100, 'currency') }}</span>
          </div>
          <div class="total-row total-final">
            <span>{{ t('Total') }}</span>
            <span>{{ n((order.so_order_total || 0) / 100, 'currency') }}</span>
          </div>
        </div>
      </div>

      <!-- Right: Payment panel -->
      <div class="checkout-payment" data-tutorial="checkout-payment">
        <!-- Payment header with status badge -->
        <div class="payment-header">
          <h3>{{ t('Pagamento') }}</h3>
          <span class="financial-badge" :class="'fs-' + order.so_order_financial_status">
            {{ checkout.financialStatusLabel(order.so_order_financial_status) }}
          </span>
        </div>

        <!-- Financial summary cards -->
        <div class="financial-cards">
          <div class="fin-card fin-card-total">
            <span class="fin-label">{{ t('Total') }}</span>
            <span class="fin-value">{{ n((order.so_order_total || 0) / 100, 'currency') }}</span>
          </div>
          <div class="fin-card fin-card-received">
            <span class="fin-label">{{ t('Recebido') }}</span>
            <span class="fin-value">{{ n((order.so_order_total_recebido || 0) / 100, 'currency') }}</span>
          </div>
          <div class="fin-card" :class="valorRestante > 0 ? 'fin-card-remaining' : 'fin-card-ok'">
            <span class="fin-label">{{ t('Restante') }}</span>
            <span class="fin-value">{{ n(valorRestante / 100, 'currency') }}</span>
          </div>
        </div>

        <!--
          Discount section. Visible while there is a remaining balance (nothing
          to discount once the OS is settled) OR while a discount is applied —
          the second clause is what keeps "Remover" reachable: a 100% discount
          zeroes the total, so gating on valorRestante alone hid the very button
          that undoes it (2026-08-25).
        -->
        <div class="payment-section" v-if="order.so_order_status !== 'cancelled' && (valorRestante > 0 || (order.so_order_discount_value ?? 0) > 0)">
          <div class="section-label">{{ t('Desconto') }}</div>

          <!-- Desconto já aplicado — mostra resumo + botão remover -->
          <div v-if="(order.so_order_discount_value ?? 0) > 0" class="discount-applied-row">
            <span class="discount-applied-value">
              <i class="pi pi-tags"></i>
              {{ t('Desconto') }}: <strong>-{{ n(discountAmount / 100, 'currency') }}</strong>
            </span>
            <Button
              :label="t('Remover')"
              icon="pi pi-times"
              size="small"
              severity="danger"
              :loading="isApplyingDiscount"
              data-testid="checkout-discount-remove-btn"
              @click="handleRemoveDiscount" />
          </div>

          <!-- Sem desconto — mostra input para aplicar -->
          <div v-else class="discount-row">
            <div class="discount-type-buttons">
              <button
                type="button"
                class="discount-type-btn"
                :class="{ active: !isPercentageDiscount }"
                data-testid="checkout-discount-type-currency-btn"
                @click="checkout.handleDiscountTypeChange(false)">
                {{ userCurrencySymbol }}
              </button>
              <button
                type="button"
                class="discount-type-btn"
                :class="{ active: isPercentageDiscount }"
                data-testid="checkout-discount-type-percent-btn"
                @click="checkout.handleDiscountTypeChange(true)">
                %
              </button>
            </div>
            <div class="discount-input-wrapper">
              <input-currency
                v-if="!isPercentageDiscount"
                v-model="discountValue"
                size="small"
                elem-id="checkout-discount-currency"
                data-testid="checkout-discount-currency-input" />
              <input-number
                elem-id="checkout-discount-percent"
                data-testid="checkout-discount-percent-input"
                v-else
                v-model="discountValue"
                suffix="%"
                :min="0"
                :max="100"
                size="small" />
            </div>
            <Button
              :label="t('Aplicar')"
              size="small"
              severity="success"
              :loading="isApplyingDiscount"
              :disabled="discountValue <= 0"
              data-testid="checkout-discount-apply-btn"
              @click="handleApplyDiscount" />
          </div>
        </div>

        <!-- Recebimentos panel (visible regardless of status, except cancelled) -->
        <PDVCheckoutRecebimentosPanel
          v-if="order"
          :order-id="order.so_order_id"
          :franchise-id="order.so_order_franchise_id"
          :valor-restante="valorRestante"
          :order-status="order.so_order_status"
        />

        <!-- Action buttons inside payment panel -->
        <div class="payment-actions" data-tutorial="checkout-actions">
          <Button
            data-testid="checkout-descancelar-btn"
            v-if="order.so_order_status === 'cancelled' && canUncancelOS"
            :label="t('Descancelar OS')"
            icon="pi pi-replay"
            size="small"
            severity="secondary"
            text
            :disabled="isUncancelling"
            @click="handleDescancelar" />
          <Button
            data-testid="checkout-cancelar-btn"
            data-tutorial="checkout-btn-cancelar"
            v-if="order.so_order_status !== 'entregue' && order.so_order_status !== 'cancelled'"
            :label="t('Cancelar OS')"
            icon="pi pi-times"
            size="small"
            severity="danger"
            text
            :disabled="!canCancelThisOS"
            v-tooltip.top="cancelBlockedTooltip"
            @click="handleCancelar" />
          <Button
            v-if="order.so_order_status === 'draft'"
            :label="t('Gerar OS')"
            icon="pi pi-check"
            size="small"
            severity="success"
            :disabled="groupedItems.length === 0 || hasExistingWorkOrders"
            v-tooltip.top="gerarBlockedTooltip"
            data-testid="checkout-btn-gerar"
            data-tutorial="checkout-btn-gerar"
            @click="handleConfirmar" />
          <span
            v-if="baasEnabled && order.so_order_status !== 'draft'"
            v-tooltip.top="fiscalGatingTooltip"
            class="checkout-emitir-factura-wrap">
            <Button
              :label="nfeLoading ? t('Emitindo...') : t('Emitir Nota Fiscal')"
              icon="pi pi-file"
              size="small"
              severity="info"
              :disabled="nfeLoading || fiscalConfigChecking || fiscalConfigured !== true"
              :loading="nfeLoading"
              data-testid="checkout-btn-emitir-nfe"
              @click="handleEmitirNotaFiscal" />
          </span>
        </div>

        <!-- Notas fiscais emitidas para esta OS — vivas, canceladas e com erro.
             Ancora ao lado do botão de emissão para responder "onde eu vejo a nota?"
             na mesma tela onde ela foi emitida (nunca noutro menu). Refresca no
             mesmo evento 'pdv.saved' que o handler de emissão já dispara. -->
        <PDVCheckoutInvoicesPanel
          v-if="baasEnabled && order.so_order_status !== 'draft'"
          :order-id="order.so_order_id"
        />
      </div>
    </div>

    <!-- Loading state -->
    <div class="loading-state" v-if="loading">
      <i class="pi pi-spinner pi-spin"></i>
      <span>{{ t('Carregando...') }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, toRef, onMounted, onBeforeUnmount, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { eventBus } from '../../eventBus';
import { useCheckout } from '../../composables/useCheckout';
import { useSOCheckout } from '../../composables/useSOCheckout';
import { useSOStatusMap } from '../../composables/useSOStatusMap';
import { useUserPermissions } from '../../composables/usePermissions';
import { useConfirmAction } from '../../composables/useConfirmAction';
import { useDateFormatter } from '../../composables/useDateFormatter';
import { permissionsState } from '../../services/permissionsService';
import { usePaymentInvoices } from '../../composables/usePaymentInvoices';
import { error as logError } from '../../utils/DebugLogger';
import { config } from '../../services/configService';
import { useClienteCombos } from '../../composables/useClienteCombos';
import { comboExplanations } from '../so-combos/explanations';
// Feature `baas`: NF issuance rides on the subaccount fiscal config (fiscal/BaaS split is a known follow-up).
const baasEnabled = config.isFeatureEnabled('baas');
const combosEnabled = config.isFeatureEnabled('combos');

type Id = number | string;

const props = withDefaults(defineProps<{
  orderId: number | string;
  host?: 'page' | 'modal';
}>(), {
  host: 'page',
});

const emit = defineEmits<{
  done: [];
  dirty: [];
}>();

const { t, n } = useI18n();
const { formatDate, fromApiDateTimeFormat } = useDateFormatter();
const formatExpectedDelivery = (val: any): string => {
  if (!val) return '';
  const d = fromApiDateTimeFormat(val);
  return d ? formatDate(d) : '';
};
const router = useRouter();
const { getItemStatus } = useSOStatusMap();

const orderIdRef = toRef(props, 'orderId');
const checkout = useCheckout(orderIdRef);

const valorRestante = computed(() => checkout.valorRestante.value);
const discountAmount = computed(() => checkout.discountAmount.value);

// F1: add-items affordance is allowed in every status except 'cancelled'
// (including 'entregue'); the warning banner appears once the OS is non-draft
// or has received payments, signalling that lowering edits may be blocked by
// the backend received-floor invariant.
const canAddItems = computed(() => checkout.canAddItems.value);
// R1: block reconfirming an order revived by uncancel — its work orders are
// already alive and confirmOrder() is not idempotent (backend refuses too).
const hasExistingWorkOrders = computed(() => checkout.hasExistingWorkOrders.value);
const gerarBlockedTooltip = computed<string | undefined>(() => {
  if (!hasExistingWorkOrders.value) return undefined;
  return t('Esta OS já possui ordens de trabalho — não é possível gerar novamente.');
});
const showPaidEditWarning = computed(
  () => checkout.order.value?.so_order_status !== 'draft' || checkout.isPaid.value,
);
// The banner must never claim recebimentos that don't exist (a generated OS with
// zero payments showed the "possui recebimentos" text — reported live). The full
// received-floor warning only renders when totalRecebido > 0; otherwise the milder
// generated-OS warning renders.
const paidEditWarningText = computed(() =>
  checkout.isPaid.value
    ? t('Esta OS já foi gerada e possui recebimentos — alterações podem afetar o valor total e não podem reduzi-lo abaixo do valor já recebido.')
    : t('Esta OS já foi gerada — alterações podem afetar o valor total.'),
);

// Re-bind discount v-model from checkout refs
const isPercentageDiscount = checkout.isPercentageDiscount;
const discountValue = checkout.discountValue;
const isApplyingDiscount = checkout.isApplyingDiscount;

// Re-bind reactive state for template access
const order = checkout.order;
const groupedItems = checkout.groupedItems;
const decoratedGroupedItems = computed(() =>
  groupedItems.value.map((item: Record<string, unknown>) => ({
    ...item,
    _so_order_item_label: `${t('Item ID')}: ${item.so_order_item_id}`,
  })),
);
const loading = checkout.loading;
const formattedClientPhone = checkout.formattedClientPhone;

const { canCancelOS, isManagerOrAbove, canUncancelOS, canUseCombos } = useUserPermissions();
const { confirmAction } = useConfirmAction();
const { desvincular: desvincularCombo } = useClienteCombos();

// Vender e usar combo: feature ligada e grupo de balcão (Operador fora).
const combosAtivos = computed<boolean>(() => combosEnabled && canUseCombos.value);
const EX_COMBO = computed(() => comboExplanations(t));

// Cancel gate, mirroring so/orders.php::cancel in BOTH its halves: the group
// gate (Full/Franqueado/Gerente/Atendente — Operador is excluded from cancel),
// plus the money check, which Atendente additionally needs: the OS must have
// taken no money. Managers cancel regardless.
const canCancelThisOS = computed<boolean>(
  () => canCancelOS.value && (isManagerOrAbove.value || checkout.totalRecebido.value === 0),
);
const cancelBlockedTooltip = computed<string | undefined>(() => {
  if (canCancelThisOS.value) return undefined;
  if (!canCancelOS.value) return t('Apenas gerentes podem cancelar — solicite à gerência.');
  return t('OS com recebimento — apenas a gerência pode cancelar.');
});
const { gerarNFeParaOrdemServico, isFiscalConfigured } = usePaymentInvoices();

// NFe emission state
const nfeLoading = ref(false);

// Uncancel-OS double-click guard, mirroring nfeLoading.
const isUncancelling = ref(false);

// Fiscal-config gating for the Emitir NFe button.
// `fiscalConfigured` is null while the per-SO check is in flight;
// the button stays disabled until we know either way.
const fiscalConfigured = ref<boolean | null>(null);
const fiscalConfigChecking = ref<boolean>(false);
const lastWhatsAppPayload = ref<Record<string, unknown> | null>(null);

async function refreshFiscalConfigStatus(franchiseId: number | string | null | undefined): Promise<void> {
  if (!baasEnabled || !franchiseId) {
    fiscalConfigured.value = null;
    return;
  }
  fiscalConfigChecking.value = true;
  try {
    fiscalConfigured.value = await isFiscalConfigured(Number(franchiseId));
  } catch {
    fiscalConfigured.value = false;
  } finally {
    fiscalConfigChecking.value = false;
  }
}

const fiscalGatingTooltip = computed<string>(() => {
  if (fiscalConfigChecking.value) return t('Verificando configuração fiscal...');
  if (fiscalConfigured.value === false) {
    return t('Franquia sem configuração fiscal — configure em Subcontas › Configuração Fiscal antes de emitir.');
  }
  return '';
});

function handleFiscalConfigUpdated(payload?: { franchiseId?: number }): void {
  // Re-check whenever the user saves the modal — covers the "configure now,
  // emit immediately" path without a page reload.
  const target = payload?.franchiseId ?? order.value?.so_order_franchise_id;
  if (target) {
    refreshFiscalConfigStatus(target);
  }
}

const userCurrencySymbol = computed(() => {
  const locale = (permissionsState as any)?.normalizedLocale || 'pt-BR';
  const localeToCurrency: Record<string, string> = {
    'pt-BR': 'BRL', 'en-US': 'USD', 'de-DE': 'EUR',
    'en-IN': 'INR', 'ja-JP': 'JPY', 'es-ES': 'EUR',
    'fr-FR': 'EUR', 'it-IT': 'EUR', 'en-GB': 'GBP',
    'zh-CN': 'CNY', 'es-AR': 'ARS', 'es-MX': 'MXN', 'es-CL': 'CLP',
    'es-EC': 'USD',
  };
  const currency = localeToCurrency[locale] || 'BRL';
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency, currencyDisplay: 'narrowSymbol' })
      .formatToParts(0)
      .find(p => p.type === 'currency')?.value || (currency === 'BRL' ? 'R$' : '$');
  } catch {
    return currency === 'BRL' ? 'R$' : '$';
  }
});

onMounted(async () => {
  window.addEventListener('beforeunload', handleBeforeUnload);
  if (props.orderId) {
    await checkout.loadOrder();
  }
  eventBus.on('pdv.item-added', handleItemAdded);
  eventBus.on('pdv.desconto-applied', checkout.loadOrder);
  eventBus.on('so-order-item.updated', handleItemAdded);
  eventBus.on('recebimento.refresh', checkout.loadOrder);
  eventBus.on('fiscal-config.updated', handleFiscalConfigUpdated);
  eventBus.on('pdv.cancelar-os.confirmed', handleCancelarConfirmed);
});

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload);
  eventBus.off('pdv.item-added', handleItemAdded);
  eventBus.off('pdv.desconto-applied', checkout.loadOrder);
  eventBus.off('so-order-item.updated', handleItemAdded);
  eventBus.off('recebimento.refresh', checkout.loadOrder);
  eventBus.off('fiscal-config.updated', handleFiscalConfigUpdated);
  eventBus.off('pdv.cancelar-os.confirmed', handleCancelarConfirmed);
});

// Re-check fiscal config whenever the loaded SO's franchise changes or the
// status leaves 'draft' (which makes the Emitir Factura button visible).
watch(
  () => [
    order.value?.so_order_franchise_id ?? null,
    order.value?.so_order_status ?? null,
  ] as const,
  ([franchiseId, status]) => {
    if (status && status !== 'draft' && franchiseId) {
      refreshFiscalConfigStatus(franchiseId);
    } else {
      fiscalConfigured.value = null;
    }
  },
  { immediate: true }
);

// ---------------------------------------------------------------------------
// Handlers
// ---------------------------------------------------------------------------
const handleBeforeUnload = (e: BeforeUnloadEvent): void => {
  if (order.value?.so_order_status === 'draft' && groupedItems.value.length > 0) {
    e.preventDefault();
    e.returnValue = '';
  }
};
function handleVoltar(): void {
  if (props.host === 'page') {
    router.push({ name: 'PDV' });
  }
}

function handleClientClick(): void {
  if (order.value?.so_order_client_id) {
    eventBus.emit('modal.cliente.open', { id: order.value.so_order_client_id });
  }
}

function handleAddServico(): void {
  if (!order.value) return;
  eventBus.emit('modal.pdv-servico-wizard.open', {
    orderId: order.value.so_order_id,
    franchiseId: order.value.so_order_franchise_id,
  });
}

function handleEditServico(item: Record<string, unknown>): void {
  if (!order.value) return;
  eventBus.emit('modal.pdv-servico-wizard.open', {
    orderId: order.value.so_order_id,
    franchiseId: order.value.so_order_franchise_id,
    editItem: item,
    children: (item.children as Record<string, unknown>[]) || [],
  });
}

function handleAddProdutos(): void {
  if (!order.value) return;
  eventBus.emit('modal.pdv-produtos-wizard.open', {
    orderId: order.value.so_order_id,
    franchiseId: order.value.so_order_franchise_id,
  });
}

function handleVenderCombo(): void {
  if (!order.value?.so_order_client_id) return;
  eventBus.emit('modal.pdv-vender-combo.open', {
    orderId: order.value.so_order_id,
    franchiseId: order.value.so_order_franchise_id,
  });
}

function handleDesvincularCombo(item: Record<string, unknown>): void {
  confirmAction({
    header: t('Desvincular combo'),
    message: t('O par volta para o saldo do combo e o item volta ao preço da matriz. Confirma?'),
    acceptLabel: t('Sim, desvincular'),
    rejectLabel: t('Voltar'),
    onAccept: async () => {
      try {
        await desvincularCombo(Number(item.so_order_item_id));
        eventBus.emit('globalMessage.success', t('Combo desvinculado do item'));
        await checkout.loadOrder();
      } catch (err) {
        const message = err instanceof Error ? err.message : '';
        eventBus.emit('globalMessage.error', message || t('Erro ao desvincular o combo'));
      }
    },
  });
}

async function handleRemoveItem(itemId: unknown): Promise<void> {
  if (!itemId) return;
  try {
    await checkout.removeItem(itemId as string | number);
    eventBus.emit('globalMessage.success', t('Item removido'));
  } catch (err) {
    // The received-floor rejection (and any other failure) is surfaced verbatim
    // by checkout.removeItem via globalMessage.error; only log here so the toast
    // is not duplicated.
    logError('PDVCheckoutForm', 'handleRemoveItem', 'Error', err);
  }
}

async function handleConfirmar(): Promise<void> {
  try {
    // Snap data before confirm (order transitions on success)
    const clienteId = Number(order.value?.so_order_client_id ?? 0);
    const clienteNome = order.value?.so_order_client_name ?? '';
    const clienteDdi = order.value?.so_order_client_ddi ?? '';
    const clienteDdd = order.value?.so_order_client_ddd ?? '';
    const clientePhone = order.value?.so_order_client_phone ?? '';
    const clienteTelefone = `${clienteDdi}${clienteDdd}${clientePhone}`;
    const orderId = order.value?.so_order_id;

    await checkout.confirmOrder();
    eventBus.emit('globalMessage.success', t('OS gerada com sucesso'));
    eventBus.emit('pdv.saved');
    emit('done');

    // ── WhatsApp trigger (fire-and-forget, best-effort) ──────
    sendPostOsWhatsApp(clienteId, clienteNome, clienteTelefone, orderId);
  } catch (err) {
    const message = err instanceof Error ? err.message : (err as { message?: string })?.message;
    eventBus.emit('globalMessage.error', message || t('Erro ao gerar OS'));
  }
}

function sendPostOsWhatsApp(
  clienteId: number,
  clienteNome: string,
  clienteTelefone: string,
  orderId?: Id,
): void {
  if (!clienteId || !clienteTelefone) return;

  try {
    // O link de pré-cadastro NÃO é mais oferecido na criação da OS — ele é
    // ofertado na criação do cliente com cadastro incompleto (useClienteFormSave).
    // Aqui vai apenas a confirmação da OS.
    const payload = {
      tipo: 'pos_os',
      clienteId,
      clienteNome,
      clienteTelefone,
      isNewClient: false,
      numeroOs: orderId != null ? String(orderId) : undefined,
    };
    lastWhatsAppPayload.value = payload;
    eventBus.emit('whatsapp.last-payload', payload);
    eventBus.emit('modal.pdv-enviar-whatsapp.open', payload);
  } catch (_err) {
    logError('PDVCheckoutForm', 'sendPostOsWhatsApp', 'Failed', _err);
    eventBus.emit('globalMessage.warning', t('OS gerada, mas não foi possível preparar o envio do WhatsApp.'));
  }
}

function reenviarWhatsApp(): void {
  if (lastWhatsAppPayload.value) {
    eventBus.emit('modal.pdv-enviar-whatsapp.open', lastWhatsAppPayload.value);
  }
}

function handleCancelar(): void {
  // The reason is mandatory for every group, so the cancel always goes through
  // the confirmation modal; it emits back on pdv.cancelar-os.confirmed.
  eventBus.emit('modal.pdv-cancelar-os.open');
}

async function handleCancelarConfirmed(payload?: { reason?: string }): Promise<void> {
  const reason = payload?.reason?.trim();
  if (!reason) return;
  try {
    await checkout.cancelOrder(reason);
    eventBus.emit('globalMessage.success', t('OS cancelada'));
    eventBus.emit('pdv.saved');
    if (props.host === 'page') {
      router.push({ name: 'PDV' });
    }
    emit('done');
  } catch (err) {
    const message = err instanceof Error ? err.message : (err as { message?: string })?.message;
    eventBus.emit('globalMessage.error', message || t('Erro ao cancelar OS'));
  }
}

function handleDescancelar(): void {
  // No campo a coletar — Orders_UncancelInput é vazio — então a confirmação
  // usa o padrão do repo para atos destrutivos sem formulário (useConfirmAction),
  // não o modal de motivo do cancelar. Os efeitos reais têm que estar nomeados
  // na mensagem, antes do accept.
  confirmAction({
    header: t('Descancelar OS'),
    message: t('A OS volta para o status Em Caixa, as ordens de trabalho canceladas são reabertas e os produtos devolvidos pelo cancelamento são consumidos novamente do estoque. Confirma?'),
    acceptLabel: t('Sim, descancelar'),
    rejectLabel: t('Voltar'),
    onAccept: async () => {
      isUncancelling.value = true;
      try {
        await checkout.uncancelOrder();
        eventBus.emit('globalMessage.success', t('OS descancelada'));
        eventBus.emit('pdv.saved');
      } catch (err) {
        const message = err instanceof Error ? err.message : (err as { message?: string })?.message;
        eventBus.emit('globalMessage.error', message || t('Erro ao descancelar OS'));
      } finally {
        isUncancelling.value = false;
      }
    },
  });
}

async function handleEmitirNotaFiscal(): Promise<void> {
  if (!order.value?.so_order_id) return;
  nfeLoading.value = true;
  try {
    const result = await gerarNFeParaOrdemServico(Number(order.value.so_order_id)) as { invoice_number?: string } | undefined;
    const number = result?.invoice_number;
    const message = number
      ? `${t('Fatura')} #${number} ${t('emitida com sucesso')}`
      : t('Fatura emitida com sucesso');
    eventBus.emit('globalMessage.success', message);
    eventBus.emit('pdv.saved');
  } catch (err) {
    const message = err instanceof Error ? err.message : (err as { message?: string })?.message;
    eventBus.emit('globalMessage.error', message || t('Erro ao emitir factura'));
  } finally {
    nfeLoading.value = false;
  }
}

function handleLaudoEntrada(item: Record<string, unknown>): void {
  if (!item.so_order_item_id) return;
  // Open detail modal on Laudos tab instead of navigating away
  eventBus.emit('modal.so-order-item.open', { itemId: item.so_order_item_id, initialTab: 'laudos' });
}

function handleLaudoSaida(item: Record<string, unknown>): void {
  if (!item.so_order_item_id) return;
  // Open detail modal on Laudos tab instead of navigating away
  eventBus.emit('modal.so-order-item.open', { itemId: item.so_order_item_id, initialTab: 'laudos' });
}

function handleItemDetail(item: Record<string, unknown>): void {
  if (!item.so_order_item_id) return;
  eventBus.emit('modal.so-order-item.open', { itemId: item.so_order_item_id });
}

async function handleApplyDiscount(): Promise<void> {
  try {
    await checkout.applyDiscount();
    eventBus.emit('globalMessage.success', t('Desconto aplicado'));
  } catch (err) {
    const message = err instanceof Error ? err.message : (err as { message?: string })?.message;
    eventBus.emit('globalMessage.error', message || t('Erro ao aplicar desconto'));
  }
}

async function handleRemoveDiscount(): Promise<void> {
  if (!order.value) return;
  try {
    await checkout.apiUpdateDiscount(order.value.so_order_id, 'fixed', 0);
    discountValue.value = 0;
    await checkout.loadOrder();
    eventBus.emit('globalMessage.success', t('Desconto removido'));
  } catch (err) {
    const message = err instanceof Error ? err.message : (err as { message?: string })?.message;
    eventBus.emit('globalMessage.error', message || t('Erro ao remover desconto'));
  }
}

function handleItemAdded(): void {
  checkout.loadOrder();
}

// ---------------------------------------------------------------------------
// Items DataTable
// ---------------------------------------------------------------------------

function buildItemHtml(item: Record<string, unknown>): string {
  const parts: string[] = [];

  // Service name + priority
  const name = item.so_service_name as string || '';
  const priority = item.so_priority_name as string || '';
  if (name) {
    parts.push(`<strong>${name}${priority ? ` — ${priority}` : ''}</strong>`);
  }

  // Description
  const desc = item.so_order_item_description as string || '';
  if (desc) {
    parts.push(`<div style="font-size:0.85em;color:var(--p-text-muted-color)">${desc}</div>`);
  }

  // Par coberto por um combo do cliente (preço 0)
  if (item.so_order_item_combo_purchase_id) {
    parts.push(`<div style="font-size:0.85em;color:var(--p-text-muted-color)">${t('Coberto por combo')}</div>`);
  }

  // Attributes
  const attrs = item.attributes as Array<Record<string, unknown>> || [];
  if (attrs.length > 0) {
    const attrHtml = attrs
      .map(a => `<span style="background:var(--p-surface-100);padding:1px 6px;border-radius:4px;font-size:0.75rem"><strong>${a.so_item_attribute_name}:</strong> ${a.so_oiav_value}</span>`)
      .join(' ');
    parts.push(`<div style="display:flex;flex-wrap:wrap;gap:4px;margin-top:4px">${attrHtml}</div>`);
  }

  // Children
  const children = item.children as Array<Record<string, unknown>> || [];
  if (children.length > 0) {
    const childHtml = children
      .map(c => {
        const total = parseInt(String(c.so_order_item_total ?? 0), 10) / 100;
        return `<div style="display:flex;justify-content:space-between;padding:2px 0;font-size:0.85rem;color:var(--p-text-muted-color)"><span>${c.so_order_item_description || ''}</span><span>${n(total, 'currency')}</span></div>`;
      })
      .join('');
    parts.push(`<div style="margin-top:4px;padding-left:8px;border-left:2px solid var(--p-surface-200)">${childHtml}</div>`);
  }

  // QR code
  const qrcode = item.so_order_item_qrcode as string || '';
  if (qrcode) {
    parts.push(`<div style="font-size:0.75rem;color:var(--p-text-muted-color);margin-top:2px">QR: ${qrcode}</div>`);
  }

  // Step progress (em producao only)
  const itemStatus = item.item_display_status as string || '';
  if (itemStatus === 'em_producao') {
    const completed = item.step_completed ?? 0;
    const total = item.step_total ?? 0;
    if (Number(total) > 0) {
      parts.push(`<div style="font-size:0.75rem;color:var(--p-text-muted-color);margin-top:2px">${completed}/${total} ${t('passos')}</div>`);
    }
  }

  return parts.join('\n');
}

const itemsTableConfig = computed(() => {
  return {
    tableId: 'checkout-items',
    columns: [
      {
        data: '_so_order_item_label',
        sortField: 'so_order_item_id',
        title: '#',
        width: 80,
        sortable: true,
        renderType: 'custom_clickable_id' as const,
        clickCallback: (_value: unknown, rowData: Record<string, unknown>) => {
          eventBus.emit('modal.so-order-item.open', { itemId: rowData.so_order_item_id });
        },
      },
      {
        data: 'so_order_item_type',
        title: t('Tipo'),
        width: 85,
        filterable: false,
        renderType: 'badge' as const,
        badgeConfig: (row: Record<string, unknown>) => {
          if (row.so_order_item_type === 'combo') return { label: t('Combo'), severity: 'warn' };
          return {
            label: row.so_order_item_type === 'service'
              ? t('Serviço')
              : (row.so_order_item_type === 'additional_service' ? t('Adicional') : t('Produto')),
            severity: row.so_order_item_type === 'service' ? 'info'
              : (row.so_order_item_type === 'additional_service' ? 'success' : 'secondary'),
          };
        },
      },
      {
        data: 'so_service_name',
        title: t('Item'),
        sortable: true,
        filterable: false,
        cellRenderer: ({ data: row }: { data: Record<string, unknown> }): string => buildItemHtml(row),
      },
      {
        data: 'item_display_status',
        title: t('Status'),
        width: 135,
        sortable: true,
        filterable: false,
        renderType: 'badge' as const,
        badgeConfig: (row: Record<string, unknown>) => getItemStatus(row.item_display_status as string),
      },
      {
        data: null,
        title: t('Valor'),
        width: 100,
        filterable: false,
        cellRenderer: ({ data: row }: { data: Record<string, unknown> }): string => {
          const total = checkout.getItemSubtotal(row) / 100;
          return n(total, 'currency');
        },
      },
      ...(combosAtivos.value ? [{
        data: null,
        title: t('Combo'),
        width: 120,
        filterable: false,
        renderType: 'clickable_button' as const,
        buttonText: t('Desvincular'),
        buttonIcon: 'pi pi-link',
        buttonAction: 'desvincular-combo',
        buttonTooltip: () => EX_COMBO.value.desvincular.what,
        buttonCondition: (row: Record<string, unknown>) => !!row.so_order_item_combo_purchase_id,
        buttonTestId: (row: Record<string, unknown>) => `checkout-desvincular-combo-${row.so_order_item_id}`,
      }] : []),
    ],
    pagination: false,
    size: 'small',
    stripedRows: false,
    showGridlines: false,
    sortable: true,
    showFilters: false,
    heightMode: 'auto',
  };
});

function handleTableButtonClick(payload: { value?: unknown; data?: Record<string, unknown>; action?: string }): void {
  if (!payload.data || !payload.action) return;
  const row = payload.data;
  switch (payload.action) {
    case 'edit':
      handleEditServico(row);
      break;
    case 'remove':
      handleRemoveItem(row.so_order_item_id);
      break;
    case 'laudo-entrada':
      handleLaudoEntrada(row);
      break;
    case 'laudo-saida':
      handleLaudoSaida(row);
      break;
    case 'item-detail':
      handleItemDetail(row);
      break;
    case 'desvincular-combo':
      handleDesvincularCombo(row);
      break;
  }
}

</script>

<style scoped>
.pdv-checkout-shell {
  padding: 0;
}

.checkout-header {
  display: flex;
  align-items: center;
  gap: 16px;
  background: var(--p-surface-0);
  border: 1px solid var(--p-surface-200);
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 16px;
}

.host-modal .checkout-header {
  margin-bottom: 0;
}

.checkout-header .client-info {
  flex: 1;
}

.client-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.client-name {
  font-weight: 600;
  font-size: 1.1rem;
}

.client-name-clickable {
  cursor: pointer;
  color: var(--p-primary-color);
  text-decoration: underline;
}

.client-name-clickable:hover {
  color: var(--p-primary-hover-color);
}

.client-phone {
  color: var(--p-text-muted-color);
}

.order-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.order-number {
  font-weight: 700;
  font-size: 1.1rem;
}

.order-status {
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: 600;
}

.status-draft { background: #fef3cd; color: #856404; }
.status-aguardando_laudos_entrada { background: #cce5ff; color: #004085; }
.status-em_producao { background: #cce5ff; color: #004085; }
.status-aguardando_laudos_saida { background: #cce5ff; color: #004085; }
.status-pronto_para_entrega { background: #d4edda; color: #155724; }
.status-parcialmente_entregue { background: #fef3cd; color: #856404; }
.status-entregue { background: #d4edda; color: #155724; }
.status-cancelled { background: #f8d7da; color: #721c24; }

.checkout-layout {
  display: grid;
  grid-template-columns: 1fr 340px;
  gap: 16px;
  margin-bottom: 16px;
}

/* Stack columns when the available width can't comfortably fit items + 320px sidebar.
   900px is roughly: items col ~520px minimum + 320px sidebar + 16px gap + paddings. */
@media (max-width: 900px) {
  .checkout-layout {
    grid-template-columns: 1fr;
  }
}

.host-modal .checkout-layout {
  max-height: calc(90vh - 80px);
  overflow-y: auto;
  margin-bottom: 0;
}

.checkout-items,
.checkout-payment {
  background: var(--p-surface-0);
  border: 1px solid var(--p-surface-200);
  border-radius: 8px;
  padding: 16px;
  /* Allow grid columns to shrink below their content's min-content width
     so long service names with white-space:nowrap don't force the grid wider
     than the modal (was causing horizontal scrollbar). */
  min-width: 0;
}

/* Drive the item-card layout off the items column's own width via a container
   query — so we stack the row vertically whenever the column is narrow,
   regardless of whether the column is part of a two-column layout or full-width. */
.checkout-items {
  container-type: inline-size;
  container-name: checkout-items;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.section-header h3 {
  margin: 0;
}

.item-actions {
  display: flex;
  gap: 8px;
}

.item-card {
  border: 1px solid var(--p-surface-200);
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 8px;
}

.item-main-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.item-info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.item-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.item-id-badge {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--p-text-muted-color);
}

.item-type-badge {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
}

.type-service { background: #cce5ff; color: #004085; }
.type-product { background: #e2e3e5; color: #383d41; }
.type-additional_service { background: #d4edda; color: #155724; }

.item-service-label {
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-qrcode {
  color: var(--p-text-muted-color);
  font-size: 0.85rem;
}

.item-status-badge {
  font-size: 0.7rem;
  padding: 2px 6px;
}

.step-progress {
  font-size: 0.75rem;
  color: var(--p-text-muted-color);
}

.item-description-row {
  margin-top: 6px;
  padding-left: 4px;
}

.item-custom-description {
  font-size: 0.9rem;
  color: var(--p-text-muted-color);
  display: block;
}

.item-attributes-row {
  margin-top: 6px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding-left: 4px;
}

.item-children {
  padding-left: 16px;
  margin-top: 8px;
  border-top: 1px dashed var(--p-surface-200);
  padding-top: 8px;
}

.child-item {
  display: flex;
  justify-content: space-between;
  font-size: 0.9rem;
  color: var(--p-text-muted-color);
  padding: 2px 0;
}

.item-total {
  font-weight: 700;
  font-size: 1rem;
  color: var(--p-text-color);
  min-width: 80px;
  text-align: right;
}

/* Narrow-column layout: stack the item row so badges, name, qrcode, and
   actions each get a dedicated line instead of overflowing horizontally.
   Drives off the .checkout-items container's own width, not the viewport,
   so this fires whenever the items column itself is cramped — including
   in a two-column desktop layout where the items column happens to be narrow. */
@container checkout-items (max-width: 700px) {
  .item-main-row {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }

  .item-info {
    flex-wrap: wrap;
    gap: 6px;
  }

  /* Service name + price gets its own full-width line; allow wrap */
  .item-service-label {
    flex-basis: 100%;
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
  }

  /* QR code, when present, also takes its own line */
  .item-qrcode {
    flex-basis: 100%;
  }

  .item-right {
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 6px;
  }

  /* Total no longer needs the 80px minimum on its own line */
  .item-total {
    min-width: auto;
    text-align: right;
    margin-right: auto;
  }
}

.empty-items {
  text-align: center;
  padding: 40px;
  color: var(--p-text-muted-color);
}

.empty-items i {
  font-size: 2rem;
  margin-bottom: 8px;
}

.totals-section {
  border-top: 2px solid var(--p-surface-200);
  padding-top: 12px;
  margin-top: 12px;
}

.total-row {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
}

.total-final {
  font-weight: 700;
  font-size: 1.1rem;
}

.discount {
  color: #dc3545;
}

/* Payment panel */
.payment-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.payment-header h3 {
  margin: 0;
}

.financial-badge {
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
}

.fs-a_receber { background: #f8d7da; color: #721c24; }
.fs-recebimento_parcial { background: #fef3cd; color: #856404; }
.fs-recebido { background: #d4edda; color: #155724; }

.financial-cards {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 8px;
  margin-bottom: 12px;
}

.fin-card {
  border-radius: 8px;
  padding: 10px 12px;
  text-align: center;
}

.fin-card-total {
  background: var(--p-surface-50, #f8f9fa);
  border: 1px solid var(--p-surface-200);
}

.fin-card-received {
  background: #d4edda;
  border: 1px solid #c3e6cb;
}

.fin-card-remaining {
  background: #f8d7da;
  border: 1px solid #f5c6cb;
}

.fin-card-ok {
  background: #d4edda;
  border: 1px solid #c3e6cb;
}

.fin-label {
  display: block;
  font-size: 0.75rem;
  color: var(--p-text-muted-color);
  margin-bottom: 2px;
}

.fin-value {
  display: block;
  font-weight: 700;
  font-size: 0.95rem;
}

.payment-section {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--p-surface-200);
}

.section-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--p-text-muted-color);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.discount-row {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 8px;
  align-items: end;
}

.discount-type-buttons {
  display: flex;
  border: 1px solid var(--p-surface-300);
  border-radius: 6px;
  overflow: hidden;
  height: 34px;
}

.discount-type-btn {
  padding: 0 16px;
  border: none;
  background: var(--p-surface-0);
  color: var(--p-text-muted-color);
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
}

.discount-type-btn:not(:last-child) {
  border-right: 1px solid var(--p-surface-300);
}

.discount-type-btn:hover {
  background: var(--p-surface-100);
}

.discount-type-btn.active {
  background: var(--p-primary-color);
  color: white;
}

.discount-input-wrapper {
  min-width: 0;
}

/* Discount applied row — summary + remove button */
.discount-applied-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  background: #fff5f5;
  border: 1px solid #fecaca;
  border-radius: 8px;
}

.discount-applied-value {
  font-size: 0.875rem;
  color: #dc3545;
  display: flex;
  align-items: center;
  gap: 6px;
}

.discount-applied-value i {
  font-size: 0.9rem;
}

.discount-input-wrapper :deep(.input-number-wrapper),
.discount-input-wrapper :deep(.input-group-wrapper) {
  margin-bottom: 0;
}

.discount-row .p-button {
  height: 34px;
}

.payment-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--p-surface-200);
}

.payment-actions .p-button {
  width: 100%;
  justify-content: center;
}

.loading-state {
  text-align: center;
  padding: 60px;
  color: var(--p-text-muted-color);
}

.loading-state i {
  font-size: 2rem;
  margin-bottom: 8px;
}
</style>
